import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import api from 'api';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { getAgreementTemplateQueryKey } from 'hooks/useAgreementTemplateQuery';
import ListingContext from '../providers/Listing/Listing.context';

export default function useRemoveAgreement(onRemoved?: () => void) {
  const { popSuccess, popServerError } = useContext(PopNotificationsContext);
  const {
    listing: { _id: listingId },
    setListing,
  } = useContext(ListingContext);

  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const removeAgreement = useCallback(async () => {
    try {
      await api.agreements.deactivateListingAgreement(listingId);

      setListing((prev) => ({ ...prev, agreementTemplateId: undefined }));
      queryClient.invalidateQueries(getAgreementTemplateQueryKey(listingId));
      popSuccess(t('agreementRemoved'));
      onRemoved?.();
    } catch (e) {
      popServerError(e);
    }
  }, [
    listingId,
    onRemoved,
    popServerError,
    popSuccess,
    queryClient,
    setListing,
    t,
  ]);

  return useCallback(async () => {
    if (
      await confirm({
        type: 'delete',
        title: t('agreementRemoveTitle'),
        content: t('agreementRemoveContent'),
        submitBtnText: t('remove'),
        cancelBtnText: t('cancel'),
      })
    )
      removeAgreement();
  }, [removeAgreement, t]);
}
